import { useMemo } from 'react';

interface TicketCategory {
  type: string;
  count: number;
  description: string;
}

export function useRepetitiveTickets(incidents: any[], catalogTasks: any[]): TicketCategory[] {
  return useMemo(() => {
    console.log("useRepetitiveTickets - Processing data");
    console.log("Incidents:", incidents?.length || 0);
    console.log("Catalog Tasks:", catalogTasks?.length || 0);
    
    // Validate input data
    const validIncidents = Array.isArray(incidents) ? incidents.filter(Boolean) : [];
    const validCatalogTasks = Array.isArray(catalogTasks) ? catalogTasks.filter(Boolean) : [];
    
    if (validIncidents.length === 0 && validCatalogTasks.length === 0) {
      console.log("No valid tickets data for repetitive tickets analysis, returning empty array");
      return [];
    }
    
    const allTickets = [...validIncidents, ...validCatalogTasks];
    console.log("Total tickets for analysis:", allTickets.length);
    
    if (allTickets.length > 0) {
      console.log("Sample ticket:", allTickets[0]);
    }
    
    // Category patterns for matching
    const categoryPatterns = [
      {
        name: "API Integration Issues",
        patterns: ['api', 'integration', 'endpoint', 'http', 'request failed', 'timeout']
      },
      {
        name: "Database Operations",
        patterns: ['database', 'sql', 'query', 'db', 'data load', 'etl']
      },
      {
        name: "Authentication Issues",
        patterns: ['auth', 'login', 'credential', 'permission', 'access denied']
      },
      {
        name: "Service Unavailable",
        patterns: ['service unavailable', '503', 'down', 'not responding']
      },
      {
        name: "Data Validation Errors",
        patterns: ['validation', 'invalid', 'format', 'malformed', 'schema']
      },
      {
        name: "System Performance",
        patterns: ['slow', 'performance', 'timeout', 'latency', 'heavy load']
      },
      {
        name: "File Processing",
        patterns: ['file', 'upload', 'download', 'document', 'attachment']
      },
      {
        name: "Deployment Issues",
        patterns: ['deploy', 'release', 'version', 'update failed']
      },
      {
        name: "Network Connectivity",
        patterns: ['network', 'connection', 'dns', 'tcp', 'timeout']
      },
      {
        name: "Email Service Issues",
        patterns: ['email', 'smtp', 'message', 'notification']
      }
    ];

    const categories = new Map<string, { count: number; description: string }>();
    let hasValidData = false;
    
    // Process real data
    allTickets.forEach(ticket => {
      if (!ticket || typeof ticket !== 'object') return;
      
      // Extract description from various possible field names
      const shortDesc = ticket.short_description || ticket['short_description'] || ticket.ShortDescription || 
                        ticket['Short description'] || '';
      const longDesc = ticket.description || ticket['description'] || ticket.Description || 
                      ticket['Description'] || '';
      
      const fullDescription = `${shortDesc} ${longDesc}`
        .toLowerCase()
        .replace(/\b(?:\d{1,2}[:/-]\d{1,2}[:/-]\d{2,4}|\d+[,.]?\d*)\b/g, '')
        .replace(/\b(?:inc|req|task|catalog|number)\d+\b/i, '')
        .trim();
        
      if (!fullDescription) return;

      // Find matching category
      let matchedCategory = 'Other';
      let highestMatchCount = 0;

      categoryPatterns.forEach(({ name, patterns }) => {
        const matchCount = patterns.filter(pattern => 
          fullDescription.includes(pattern.toLowerCase())
        ).length;

        if (matchCount > highestMatchCount) {
          highestMatchCount = matchCount;
          matchedCategory = name;
        }
      });

      const existing = categories.get(matchedCategory) || { count: 0, description: '' };
      categories.set(matchedCategory, {
        count: existing.count + 1,
        description: existing.description || fullDescription.slice(0, 100) // Keep a sample description
      });
      
      hasValidData = true;
    });
    
    // If we found valid data, return it
    if (hasValidData && categories.size > 0) {
      const result = Array.from(categories.entries())
        .map(([type, { count, description }]) => ({ type, count, description }))
        .sort((a, b) => b.count - a.count);
      
      console.log("Generated repetitive tickets data from real data:", result.length, "categories");
      return result;
    }
    
    // Return empty array if no real data could be processed
    console.log("No patterns found, returning empty array");
    return [];
  }, [incidents, catalogTasks]);
}
